import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Plus, TrendingUp, Download } from "lucide-react";
import { toast } from "sonner";
import { downloadCSV, csvCurrency, csvDate } from "../utils/csv";

import { API_URL } from "../components/pipeline/constants";
import { PipelineStats } from "../components/pipeline/PipelineStats";
import { PipelineFilters } from "../components/pipeline/PipelineFilters";
import { PipelineTable } from "../components/pipeline/PipelineTable";
import { CreateOpportunityDialog } from "../components/pipeline/CreateOpportunityDialog";
import { OpportunityDetailDialog } from "../components/pipeline/OpportunityDetailDialog";
import { QuotePdfDialog } from "../components/pipeline/QuotePdfDialog";
import { DeleteConfirmDialog } from "../components/pipeline/DeleteConfirmDialog";
import { ConvertDialog } from "../components/pipeline/ConvertDialog";
import { LoseDialog } from "../components/pipeline/LoseDialog";

const PipelinePage = () => {
  const { user, canCreate, canEdit, canDelete, canExport } = useAuth();
  const [opportunities, setOpportunities] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [catalogServices, setCatalogServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [createMode, setCreateMode] = useState("existing");
  const [newNoteText, setNewNoteText] = useState("");
  const [stageChangeNote, setStageChangeNote] = useState("");
  const [pendingStageChange, setPendingStageChange] = useState(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [showClosed, setShowClosed] = useState(false);
  
  // Customer search
  const [customerSearch, setCustomerSearch] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  
  // New opportunity form
  const [newOpp, setNewOpp] = useState({
    customer_id: "", customer_name: "", title: "", stage: "LEAD",
    estimated_mrr: "", notes: "", ejecutivo_id: "",
    proxima_accion: "", fecha_estimada_cierre: "", selected_services: [],
  });
  
  // New customer form
  const [newCustomer, setNewCustomer] = useState({
    customer_type: "RESIDENCIAL", status: "LEAD", nombre: "", apellidos: "",
    dni_nie: "", document_type: "DNI", razon_social: "", cif: "", dni_representante: "", representante_nombre: "",
    representante_document_type: "DNI",
    persona_contacto: "", email: "", phone: "", phone_country: "ES", direccion_fiscal: "",
    ciudad: "", localidad: "", codigo_postal: "", iban: "",
  });

  // Convert modal
  const [convertOpen, setConvertOpen] = useState(false);
  const [installationDate, setInstallationDate] = useState("");
  const [loseOpen, setLoseOpen] = useState(false);
  const [motivoPerdida, setMotivoPerdida] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [oppToDelete, setOppToDelete] = useState(null);
  const [serviceTab, setServiceTab] = useState("individual");
  const [builderHasItems, setBuilderHasItems] = useState(false);
  
  // PDF
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  
  // For conversion - location selection/creation per product
  const [customerLocations, setCustomerLocations] = useState([]);
  const [productLocations, setProductLocations] = useState({});
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [createNewLocation, setCreateNewLocation] = useState(false);
  const [newLocation, setNewLocation] = useState({
    nombre_sede: "", direccion_completa: "", codigo_postal: "",
    ciudad: "", contacto_tecnico_sede: "", telefono_sede: "",
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [oppsRes, customersRes, comercialesRes, catalogRes] = await Promise.all([
        axios.get(`${API_URL}/api/opportunities`),
        axios.get(`${API_URL}/api/customers`),
        axios.get(`${API_URL}/api/users/comerciales`),
        axios.get(`${API_URL}/api/catalog-services`),
      ]);
      setOpportunities(oppsRes.data);
      setCustomers(customersRes.data);
      setUsers(comercialesRes.data);
      setCatalogServices(catalogRes.data.filter(s => s.is_active));
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = useMemo(() => {
    if (!customerSearch.trim()) return [];
    const search = customerSearch.toLowerCase();
    return customers.filter(c => {
      const name = c.razon_social || `${c.nombre || ""} ${c.apellidos || ""}`.trim();
      return name.toLowerCase().includes(search) || 
             (c.customer_id || "").toLowerCase().includes(search) ||
             (c.email || "").toLowerCase().includes(search);
    }).slice(0, 10);
  }, [customers, customerSearch]);

  const filteredOpportunities = useMemo(() => {
    let filtered = opportunities;
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(opp => 
        opp.title?.toLowerCase().includes(search) || opp.customer_name?.toLowerCase().includes(search) ||
        opp.opportunity_id?.toLowerCase().includes(search) || opp.ejecutivo_nombre?.toLowerCase().includes(search)
      );
    }
    if (stageFilter !== "all") filtered = filtered.filter(opp => opp.stage === stageFilter);
    if (!showClosed) filtered = filtered.filter(opp => !["GANADA", "PERDIDA"].includes(opp.stage));
    return filtered;
  }, [opportunities, searchTerm, stageFilter, showClosed]);

  const calcOppMRR = (opp) => {
    if (!opp.selected_services?.length) return opp.estimated_mrr || 0;
    return opp.selected_services.reduce((total, svc) => {
      const qty = parseInt(svc.cantidad) || 1;
      return total + (svc.precio * qty) - (svc.descuento || 0);
    }, 0);
  };

  const handleExportOpportunities = () => {
    if (filteredOpportunities.length === 0) {
      toast.error("No hay oportunidades para exportar");
      return;
    }
    const columns = [
      { key: "opportunity_id", label: "Referencia" },
      { key: "title", label: "Título" },
      { key: "customer_name", label: "Cliente" },
      { key: "stage", label: "Etapa" },
      { key: "mrr", label: "MRR", format: (_, row) => csvCurrency(calcOppMRR(row)) },
      { key: "ejecutivo_nombre", label: "Ejecutivo" },
      { key: "proxima_accion", label: "Próxima acción" },
      { key: "fecha_estimada_cierre", label: "Cierre estimado", format: csvDate },
      { key: "created_at", label: "Creada", format: csvDate },
    ];
    downloadCSV("pipeline_oportunidades", columns, filteredOpportunities);
    toast.success(`${filteredOpportunities.length} oportunidades exportadas a CSV`);
  };

  const stats = useMemo(() => {
    const activeOpps = opportunities.filter(o => !["GANADA", "PERDIDA"].includes(o.stage));
    return {
      total: activeOpps.length,
      totalMRR: activeOpps.reduce((sum, o) => sum + calcOppMRR(o), 0),
    };
  }, [opportunities]);

  const calculateMRR = useCallback((services) => {
    return services.reduce((sum, svc) => sum + ((parseFloat(svc.precio) || 0) * (svc.cantidad || 1)), 0);
  }, []);

  const calculateMRRWithDiscount = (services) => {
    return services.reduce((total, svc) => {
      const qty = parseInt(svc.cantidad) || 0;
      return total + (svc.precio * qty) - (svc.descuento || 0);
    }, 0);
  };

  const formatCurrency = (value) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(value || 0);
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const resetNewOpp = () => {
    setNewOpp({ customer_id: "", customer_name: "", title: "", stage: "LEAD", estimated_mrr: "", notes: "", ejecutivo_id: "", proxima_accion: "", fecha_estimada_cierre: "", selected_services: [] });
    setNewCustomer({ customer_type: "RESIDENCIAL", status: "LEAD", nombre: "", apellidos: "", dni_nie: "", razon_social: "", cif: "", dni_representante: "", representante_nombre: "", persona_contacto: "", email: "", phone: "", direccion_fiscal: "", ciudad: "", localidad: "", codigo_postal: "", iban: "" });
    setCreateMode("existing");
    setCustomerSearch("");
  };

  const handleAddService = (catalogService) => {
    const newService = {
      catalog_service_id: catalogService.id, nombre: catalogService.nombre,
      categoria: catalogService.categoria, precio: catalogService.precio_base,
      permanencia: catalogService.permanencia_meses, penalizacion: catalogService.penalizacion_cancelacion,
      proveedor_id: catalogService.proveedor_id, proveedor_nombre: catalogService.proveedor_nombre,
      descuento: 0, cantidad: '',
    };
    const updatedServices = [...newOpp.selected_services, newService];
    setNewOpp({ ...newOpp, selected_services: updatedServices, estimated_mrr: calculateMRR(updatedServices) });
  };

  const handleUpdateServiceQuantity = (index, quantity) => {
    const updatedServices = [...newOpp.selected_services];
    updatedServices[index] = { ...updatedServices[index], cantidad: quantity };
    setNewOpp({ ...newOpp, selected_services: updatedServices, estimated_mrr: calculateMRR(updatedServices) });
  };

  const validateServiceQuantities = () => newOpp.selected_services.every(svc => parseInt(svc.cantidad) >= 1);

  const handleRemoveService = (index) => {
    const updatedServices = newOpp.selected_services.filter((_, i) => i !== index);
    setNewOpp({ ...newOpp, selected_services: updatedServices, estimated_mrr: calculateMRR(updatedServices) });
  };

  const handleAddPackageServices = (services) => {
    const updated = [...newOpp.selected_services, ...services];
    setNewOpp({ ...newOpp, selected_services: updated, estimated_mrr: calculateMRR(updated) });
  };

  const handleSelectCustomer = (customer) => {
    const name = customer.razon_social || `${customer.nombre || ""} ${customer.apellidos || ""}`.trim();
    setNewOpp({ ...newOpp, customer_id: customer.id, customer_name: name });
    setCustomerSearch(name);
    setShowCustomerDropdown(false);
  };

  const handleCreateOpportunity = async () => {
    if (newOpp.selected_services.length === 0 && !builderHasItems) { toast.error("Debes seleccionar al menos un servicio"); return; }
    if (!validateServiceQuantities()) { toast.error("Todos los servicios deben tener al menos 1 unidad"); return; }
    if (!newOpp.ejecutivo_id) { toast.error("El ejecutivo de ventas es obligatorio"); return; }
    // " / " (not " + ") to avoid ambiguity when product names already contain "+"
    // internally (e.g. "Móvil 25GB + Ilimitadas").
    const title = newOpp.title || newOpp.selected_services.map(s => s.nombre).join(" / ");
    if (!title) { toast.error("El título es obligatorio"); return; }
    try {
      let customerId = newOpp.customer_id;
      let customerName = newOpp.customer_name;
      if (createMode === "new") {
        const customerRes = await axios.post(`${API_URL}/api/customers`, { ...newCustomer, status: "LEAD" });
        customerId = customerRes.data.id;
        customerName = newCustomer.customer_type === "EMPRESA" ? newCustomer.razon_social : `${newCustomer.nombre} ${newCustomer.apellidos}`.trim();
        toast.success("Cliente creado");
      }
      if (!customerId) { toast.error("Selecciona o crea un cliente"); return; }
      await axios.post(`${API_URL}/api/opportunities`, {
        ...newOpp, title, customer_id: customerId, customer_name: customerName,
        estimated_mrr: calculateMRR(newOpp.selected_services),
        proxima_accion: newOpp.proxima_accion || null,
      });
      toast.success("Oportunidad creada");
      setCreateOpen(false);
      resetNewOpp();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear oportunidad");
    }
  };

  const prepareConversion = async () => {
    if (!selectedOpp) return;
    try {
      const locsRes = await axios.get(`${API_URL}/api/locations?customer_id=${selectedOpp.customer_id}`);
      setCustomerLocations(locsRes.data);
      const initialProductLocations = {};
      if (selectedOpp.selected_services) {
        let productIndex = 0;
        selectedOpp.selected_services.forEach((svc) => {
          const qty = svc.cantidad || 1;
          for (let i = 0; i < qty; i++) { initialProductLocations[productIndex] = locsRes.data.length > 0 ? locsRes.data[0].id : ""; productIndex++; }
        });
      }
      setProductLocations(initialProductLocations);

      // Prefill from saved coverage_result when available. When we captured
      // structured parts (street/postal_code/city…) we map them one-to-one
      // to the conversion form fields so the user doesn't retype them.
      // Falls back to the plain address string for legacy coverage entries.
      const cov = selectedOpp.coverage_result;
      if (cov && cov.status === "available" && (cov.address || cov.address_parts)) {
        const parts = cov.address_parts || {};
        const streetWithParts = [
          (parts.street || "").trim(),
          parts.portal ? `Portal ${parts.portal}` : "",
          parts.floor ? `Piso ${parts.floor}` : "",
          parts.door ? `Puerta ${parts.door}` : "",
        ].filter(Boolean).join(", ");
        setNewLocation(prev => ({
          ...prev,
          // Intentionally DO NOT prefill `nombre_sede` — the user must
          // choose an explicit label/name themselves. Prefilling it with
          // something like "Dirección de instalación" looked like an
          // actual value and confused users.
          direccion_completa: prev.direccion_completa ||
            streetWithParts || cov.address || "",
          codigo_postal: prev.codigo_postal || (parts.postal_code || ""),
          ciudad: prev.ciudad || (parts.city || ""),
        }));
        setCreateNewLocation(true);
      } else if (locsRes.data.length > 0) {
        setSelectedLocationId(locsRes.data[0].id);
        setCreateNewLocation(false);
      } else {
        setCreateNewLocation(true);
      }
      setInstallationDate(new Date().toISOString().split("T")[0]);
      setConvertOpen(true);
    } catch (error) { toast.error("Error al cargar ubicaciones"); }
  };

  const handleConvert = async () => {
    if (!installationDate) { toast.error("La fecha de contratación es obligatoria"); return; }
    if (createNewLocation && !newLocation.nombre_sede) { toast.error("El nombre de la sede es obligatorio"); return; }
    try {
      const result = await axios.post(`${API_URL}/api/opportunities/${selectedOpp.id}/convert`, {
        installation_date: installationDate, location_id: createNewLocation ? null : selectedLocationId,
        location_data: createNewLocation ? newLocation : null, product_locations: productLocations,
      });
      toast.success(`¡Oportunidad convertida! Servicios creados: ${result.data.services_created?.join(", ") || "N/A"}`);
      setConvertOpen(false); setDetailOpen(false); setInstallationDate("");
      setNewLocation({ nombre_sede: "", direccion_completa: "", codigo_postal: "", ciudad: "", contacto_tecnico_sede: "", telefono_sede: "" });
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Error al convertir"); }
  };

  const handleLose = async () => {
    try {
      await axios.put(`${API_URL}/api/opportunities/${selectedOpp.id}`, { stage: "PERDIDA", resultado: "PERDIDO", motivo_perdida: motivoPerdida });
      toast.success("Oportunidad marcada como perdida");
      setLoseOpen(false); setDetailOpen(false); setMotivoPerdida(""); fetchData();
    } catch (error) { toast.error("Error al actualizar"); }
  };

  const handleDeleteOpportunity = async () => {
    if (!oppToDelete) return;
    try {
      await axios.delete(`${API_URL}/api/opportunities/${oppToDelete}`);
      toast.success("Oportunidad eliminada");
      setDeleteConfirmOpen(false); setOppToDelete(null); setDetailOpen(false); fetchData();
    } catch (error) { toast.error("Error al eliminar"); }
  };

  const openDeleteConfirm = (oppId) => { setOppToDelete(oppId); setDeleteConfirmOpen(true); };

  const openDetail = (opp) => { setSelectedOpp({ ...opp }); setEditMode(false); setNewNoteText(""); setDetailOpen(true); };

  const handleAddNote = async () => {
    if (!selectedOpp || !newNoteText.trim()) return;
    try {
      const res = await axios.post(`${API_URL}/api/opportunities/${selectedOpp.id}/notes`, { text: newNoteText.trim() });
      setSelectedOpp(res.data); setNewNoteText(""); fetchData(); toast.success("Nota añadida");
    } catch (error) { toast.error("Error al añadir nota"); }
  };

  const handleStageChangeWithNote = async () => {
    if (!selectedOpp || !pendingStageChange) return;
    try {
      const payload = { stage: pendingStageChange };
      if (stageChangeNote.trim()) payload.stage_change_note = stageChangeNote.trim();
      const res = await axios.put(`${API_URL}/api/opportunities/${selectedOpp.id}`, payload);
      setSelectedOpp(res.data); setPendingStageChange(null); setStageChangeNote(""); fetchData(); toast.success("Etapa actualizada");
    } catch (error) { toast.error("Error al actualizar etapa"); }
  };

  const handleUpdateProximaAccion = async (value) => {
    try {
      await axios.put(`${API_URL}/api/opportunities/${selectedOpp.id}`, { proxima_accion: value });
      setSelectedOpp({ ...selectedOpp, proxima_accion: value }); fetchData(); toast.success("Próxima acción actualizada");
    } catch (error) { toast.error("Error al actualizar"); }
  };

  const handleUpdateEjecutivo = async (userId) => {
    const u = users.find(u => u.id === userId);
    try {
      await axios.put(`${API_URL}/api/opportunities/${selectedOpp.id}`, { ejecutivo_id: userId, ejecutivo_nombre: u?.name || "" });
      setSelectedOpp({ ...selectedOpp, ejecutivo_id: userId, ejecutivo_nombre: u?.name || "" }); fetchData(); toast.success("Ejecutivo actualizado");
    } catch (error) { toast.error("Error al actualizar ejecutivo"); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#42E695]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pipeline-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-semibold text-white flex items-center gap-3">
            <TrendingUp className="h-8 w-8 text-[#03BEE4]" />
            Pipeline de Ventas
          </h1>
          <p className="text-slate-400 mt-1">Gestión de oportunidades comerciales</p>
        </div>
        <div className="flex items-center gap-2">
          {canExport("pipeline") && (
            <Button
              onClick={handleExportOpportunities}
              variant="outline"
              className="border-[#42E695] text-[#42E695] hover:bg-[#42E695]/20"
              data-testid="export-pipeline-csv-btn"
            >
              <Download className="h-4 w-4 mr-2" />Exportar CSV
            </Button>
          )}
          {canCreate("pipeline") && (
            <Button onClick={() => setCreateOpen(true)} className="bg-[#42E695] text-[#04042C] hover:bg-[#42E695]/90" data-testid="create-opportunity-btn">
              <Plus className="h-4 w-4 mr-2" />Nueva Oportunidad
            </Button>
          )}
        </div>
      </div>

      <PipelineStats stats={stats} formatCurrency={formatCurrency} />

      <PipelineFilters
        searchTerm={searchTerm} setSearchTerm={setSearchTerm}
        stageFilter={stageFilter} setStageFilter={setStageFilter}
        showClosed={showClosed} setShowClosed={setShowClosed}
      />

      <PipelineTable
        filteredOpportunities={filteredOpportunities}
        formatCurrency={formatCurrency}
        openDetail={openDetail}
        canDelete={canDelete("pipeline")}
        openDeleteConfirm={openDeleteConfirm}
      />

      <CreateOpportunityDialog
        open={createOpen} onOpenChange={setCreateOpen}
        createMode={createMode} setCreateMode={setCreateMode}
        customerSearch={customerSearch} setCustomerSearch={setCustomerSearch}
        showCustomerDropdown={showCustomerDropdown} setShowCustomerDropdown={setShowCustomerDropdown}
        filteredCustomers={filteredCustomers} onSelectCustomer={handleSelectCustomer}
        newCustomer={newCustomer} setNewCustomer={setNewCustomer}
        newOpp={newOpp} setNewOpp={setNewOpp}
        catalogServices={catalogServices}
        onAddService={handleAddService} onRemoveService={handleRemoveService}
        onUpdateServiceQuantity={handleUpdateServiceQuantity}
        serviceTab={serviceTab} setServiceTab={setServiceTab}
        onBuilderStateChange={setBuilderHasItems}
        onAddPackageServices={handleAddPackageServices}
        users={users} formatCurrency={formatCurrency}
        calculateMRRWithDiscount={calculateMRRWithDiscount}
        onCreate={handleCreateOpportunity}
      />

      <OpportunityDetailDialog
        open={detailOpen} onOpenChange={setDetailOpen}
        selectedOpp={selectedOpp} setSelectedOpp={setSelectedOpp} customers={customers}
        users={users}
        formatCurrency={formatCurrency} formatDate={formatDate}
        pendingStageChange={pendingStageChange} setPendingStageChange={setPendingStageChange}
        stageChangeNote={stageChangeNote} setStageChangeNote={setStageChangeNote}
        onStageChangeWithNote={handleStageChangeWithNote}
        newNoteText={newNoteText} setNewNoteText={setNewNoteText}
        onAddNote={handleAddNote}
        onPrepareConversion={prepareConversion}
        onOpenLose={() => setLoseOpen(true)}
        onOpenQuoteModal={() => setQuoteModalOpen(true)}
        canDelete={canDelete("pipeline")}
        canEditCustomers={canEdit("customers")}
        onOpenDeleteConfirm={openDeleteConfirm}
        onUpdateProximaAccion={handleUpdateProximaAccion}
        onUpdateEjecutivo={handleUpdateEjecutivo}
        onRefresh={fetchData}
      />

      <QuotePdfDialog
        open={quoteModalOpen} onOpenChange={setQuoteModalOpen}
        selectedOpp={selectedOpp}
      />

      <DeleteConfirmDialog
        open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}
        onConfirm={handleDeleteOpportunity}
        onCancel={() => { setDeleteConfirmOpen(false); setOppToDelete(null); }}
      />

      <ConvertDialog
        open={convertOpen} onOpenChange={setConvertOpen}
        selectedOpp={selectedOpp}
        installationDate={installationDate} setInstallationDate={setInstallationDate}
        customerLocations={customerLocations}
        productLocations={productLocations} setProductLocations={setProductLocations}
        createNewLocation={createNewLocation} setCreateNewLocation={setCreateNewLocation}
        newLocation={newLocation} setNewLocation={setNewLocation}
        formatCurrency={formatCurrency} onConfirm={handleConvert}
      />

      <LoseDialog
        open={loseOpen} onOpenChange={setLoseOpen}
        motivoPerdida={motivoPerdida} setMotivoPerdida={setMotivoPerdida}
        onConfirm={handleLose}
      />
    </div>
  );
};

export default PipelinePage;
